import { useState, useCallback, useEffect } from 'react';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import Card, { CardHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Alert from '@/components/ui/Alert';
import { settingsApi } from '@/services/settings';
import authApi from '@/services/auth';
import { ApiClientError } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { config } from '@/config';
import type { OAuthProvider } from '@/types';

/**
 * Microsoft Logo Component for OAuth re-authentication
 */
function MicrosoftLogo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 21 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

/**
 * Get display name for OAuth provider
 */
function getProviderDisplayName(provider: OAuthProvider): string {
  switch (provider) {
    case 'google':
      return 'Google';
    case 'microsoft':
      return 'Microsoft';
    case 'apple':
      return 'Apple';
    default:
      return provider;
  }
}

/**
 * Danger Zone settings component
 * Handles destructive actions like account deletion
 *
 * WP-GDPR-1: GDPR-Compliant User Self-Deletion
 * - Requires email confirmation (must match account email)
 * - Requires password verification for security
 *
 * WP-GDPR-3: OAuth User Account Deletion
 * - Detects OAuth-only users (no password set)
 * - Shows OAuth re-authentication button instead of password field
 * - Supports Google and Microsoft OAuth re-authentication
 */
export default function DangerZoneSettings() {
  const { logout, userEmail, user } = useAuthStore();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [oauthVerified, setOauthVerified] = useState<{
    idToken: string;
    provider: OAuthProvider;
  } | null>(null);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);

  // Determine if user is OAuth-only (no password)
  const isOAuthOnly = user?.hasPassword === false;
  const primaryProvider = user?.primaryAuthMethod as OAuthProvider | undefined;

  // Check if Google OAuth is configured
  const isGoogleConfigured = Boolean(config.googleClientId);

  // Handle Google OAuth re-authentication success
  const handleGoogleSuccess = useCallback((credentialResponse: CredentialResponse) => {
    const idToken = credentialResponse.credential;
    if (idToken) {
      setOauthVerified({ idToken, provider: 'google' });
      setError(null);
    } else {
      setError('Failed to get credentials from Google. Please try again.');
    }
  }, []);

  // Handle Google OAuth error
  const handleGoogleError = useCallback(() => {
    setError('Google sign-in was cancelled or failed. Please try again.');
  }, []);

  // Handle Microsoft OAuth re-authentication
  // For deletion, we need the ID token, so we use a popup/redirect flow
  const handleMicrosoftReauth = useCallback(async () => {
    setMicrosoftLoading(true);
    setError(null);

    try {
      // Store state to know we're in deletion flow when returning from OAuth
      sessionStorage.setItem('oauth_deletion_flow', 'microsoft');
      sessionStorage.setItem('oauth_deletion_email', confirmEmail);

      // Get authorization URL and redirect
      const authUrl = await authApi.getMicrosoftAuthUrl();
      // Add a state parameter to indicate deletion flow
      const url = new URL(authUrl);
      url.searchParams.set('state', 'deletion');
      window.location.href = url.toString();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to initiate Microsoft sign-in';
      setError(message);
      setMicrosoftLoading(false);
    }
  }, [confirmEmail]);

  // Check for OAuth callback on mount (for Microsoft redirect flow)
  useEffect(() => {
    const checkOAuthCallback = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const oauthToken = urlParams.get('oauth_token');
      const provider = sessionStorage.getItem('oauth_deletion_flow') as OAuthProvider | null;
      const savedEmail = sessionStorage.getItem('oauth_deletion_email');

      if (oauthToken && provider) {
        // Clean up session storage
        sessionStorage.removeItem('oauth_deletion_flow');
        sessionStorage.removeItem('oauth_deletion_email');

        // Clean URL
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);

        // Set OAuth verified state
        setOauthVerified({ idToken: oauthToken, provider });
        setIsDeleteModalOpen(true);
        if (savedEmail) {
          setConfirmEmail(savedEmail);
        }
      }
    };

    checkOAuthCallback();
  }, []);

  const handleDeleteAccount = async () => {
    if (confirmEmail !== userEmail) {
      setError('Email does not match your account email.');
      return;
    }

    // Validate verification method
    if (isOAuthOnly) {
      if (!oauthVerified) {
        setError('Please verify your identity using your sign-in provider.');
        return;
      }
    } else {
      if (!password) {
        setError('Please enter your password.');
        return;
      }
    }

    setIsDeleting(true);
    setError(null);

    try {
      if (isOAuthOnly && oauthVerified) {
        // OAuth verification
        await settingsApi.deleteAccount({
          confirmEmail,
          oauthIdToken: oauthVerified.idToken,
          oauthProvider: oauthVerified.provider,
        });
      } else {
        // Password verification
        await settingsApi.deleteAccount({ confirmEmail, password });
      }
      logout();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to delete account. Please try again or contact support.');
      }
      setIsDeleting(false);
    }
  };

  const resetModal = () => {
    setIsDeleteModalOpen(false);
    setConfirmEmail('');
    setPassword('');
    setError(null);
    setOauthVerified(null);
  };

  // Check if delete button should be enabled
  const isDeleteEnabled = (): boolean => {
    if (confirmEmail !== userEmail) return false;
    if (isOAuthOnly) {
      return oauthVerified !== null;
    }
    return password.length > 0;
  };

  return (
    <>
      <Card>
        <CardHeader
          title="Danger Zone"
          description="Irreversible actions"
        />

        <div className="flex items-center justify-between py-3">
          <div>
            <div className="font-medium text-red-600">Delete account</div>
            <div className="text-sm text-gray-500">
              Permanently delete your account and all data
            </div>
          </div>
          <Button
            variant="danger"
            size="sm"
            onClick={() => setIsDeleteModalOpen(true)}
          >
            Delete account
          </Button>
        </div>
      </Card>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-modal-title"
        >
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 transition-opacity"
            onClick={() => !isDeleting && resetModal()}
            aria-hidden="true"
          />

          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h2
              id="delete-modal-title"
              className="text-lg font-semibold text-gray-900 mb-4"
            >
              Delete Your Account
            </h2>

            <Alert variant="error" className="mb-4">
              <strong>This action cannot be undone.</strong> All your data, including connected
              accounts, transaction history, and preferences will be permanently deleted.
            </Alert>

            {error && (
              <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
                {error}
              </Alert>
            )}

            <div className="space-y-4 mb-4">
              {/* Email confirmation - always required */}
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  To confirm, please type your email address:
                </p>
                <Input
                  type="email"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  placeholder="your@email.com"
                  disabled={isDeleting}
                  autoComplete="email"
                />
              </div>

              {/* Identity verification - password OR OAuth */}
              {isOAuthOnly ? (
                // OAuth re-authentication for OAuth-only users
                <div>
                  <p className="text-sm text-gray-600 mb-2">
                    Please verify your identity by signing in with{' '}
                    {primaryProvider ? getProviderDisplayName(primaryProvider) : 'your account'}:
                  </p>

                  {oauthVerified ? (
                    // Show verified state
                    <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <svg
                        className="w-5 h-5 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-sm text-green-700">
                        Identity verified with {getProviderDisplayName(oauthVerified.provider)}
                      </span>
                    </div>
                  ) : (
                    // Show OAuth buttons
                    <div className="space-y-2">
                      {/* Google re-auth */}
                      {(primaryProvider === 'google' || !primaryProvider) && isGoogleConfigured && (
                        <div className="w-full">
                          <GoogleLogin
                            onSuccess={handleGoogleSuccess}
                            onError={handleGoogleError}
                            useOneTap={false}
                            theme="outline"
                            size="large"
                            width={352}
                            text="continue_with"
                            shape="rectangular"
                          />
                        </div>
                      )}

                      {/* Microsoft re-auth */}
                      {(primaryProvider === 'microsoft' || !primaryProvider) && (
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full bg-white hover:bg-gray-50 border-gray-300 text-gray-700 font-medium"
                          disabled={isDeleting || microsoftLoading}
                          isLoading={microsoftLoading}
                          onClick={handleMicrosoftReauth}
                          leftIcon={
                            !microsoftLoading ? (
                              <MicrosoftLogo className="w-5 h-5" />
                            ) : undefined
                          }
                        >
                          {microsoftLoading ? 'Signing in...' : 'Continue with Microsoft'}
                        </Button>
                      )}

                      {/* Show primary provider button prominently if set */}
                      {primaryProvider && primaryProvider !== 'google' && primaryProvider !== 'microsoft' && (
                        <p className="text-sm text-gray-500 text-center">
                          {getProviderDisplayName(primaryProvider)} re-authentication coming soon.
                          Please contact support for assistance.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                // Password verification for users with password
                <div>
                  <p className="text-sm text-gray-600 mb-2">
                    Enter your password to verify your identity:
                  </p>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your password"
                    disabled={isDeleting}
                    autoComplete="current-password"
                  />
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={resetModal}
                disabled={isDeleting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={handleDeleteAccount}
                disabled={!isDeleteEnabled()}
                isLoading={isDeleting}
                className="flex-1"
              >
                Delete Account
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
